/**
 * Single-field prompt in the portal's own chrome (replaces window.prompt).
 * Enter submits, Escape cancels, the field is focused with its text selected.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { resolvePromptValue } from './dialogValue';
import { GatedButton } from '../GatedButton';

export function PromptDialog({
  title,
  label,
  body,
  initial,
  placeholder,
  hint,
  allowEmpty,
  confirmLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  label?: string;
  body?: ReactNode;
  initial: string;
  placeholder?: string;
  hint?: string;
  allowEmpty: boolean;
  confirmLabel: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const resolved = resolvePromptValue(value, { allowEmpty });
  const canSubmit = resolved !== null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mt-dialog-title"
        style={{ width: 440 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (resolved !== null) onSubmit(resolved);
        }}
      >
        <div className="modal-head">
          <h2 id="mt-dialog-title">{title}</h2>
        </div>
        <div className="modal-body">
          {body ? <div style={{ fontSize: 13, lineHeight: 1.5 }}>{body}</div> : null}
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            {label ? <span style={{ color: 'var(--muted)', fontSize: 11 }}>{label}</span> : null}
            <input
              type="text"
              className="inp"
              value={value}
              placeholder={placeholder}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setValue(e.target.value)}
              aria-label={label ?? title}
            />
            {hint ? <span style={{ color: 'var(--muted)', fontSize: 11 }}>{hint}</span> : null}
          </label>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
          <GatedButton
            type="submit"
            className="btn primary"
            reason={canSubmit ? null : `Enter ${label ? label.toLowerCase() : 'a value'} first`}
          >
            {confirmLabel}
          </GatedButton>
        </div>
      </form>
    </div>
  );
}
